import { defineStore } from 'pinia'
import { ref, shallowRef, watch } from 'vue'

export interface WorkbookSourceContext {
  projectId: number
  workbookId: string
  filename: string
}

// Persists the operator's preferred split between the source-card pane (left)
// and the workbook grid (right) in the Review Workbook viewer. Clamped to
// [MIN, MAX] so a fat-finger drag can't collapse either pane. Deliberately
// separate from the extraction workspace's pane store — the two surfaces are
// used differently and shouldn't share a width preference.

const STORAGE_KEY = 'moria:review-workbook-pane-left-percent'
const MIN = 20
const MAX = 80
const DEFAULT = 50

function readInitial(): number {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return DEFAULT
    const parsed = Number(raw)
    if (!Number.isFinite(parsed)) return DEFAULT
    return Math.min(MAX, Math.max(MIN, parsed))
  } catch {
    return DEFAULT
  }
}

export const useReviewWorkbookPaneStore = defineStore('reviewWorkbookPane', () => {
  const leftPercent = ref<number>(readInitial())
  const source = shallowRef<WorkbookSourceContext | null>(null)

  function setSource(value: WorkbookSourceContext): void {
    source.value = value
  }

  function clearSource(value: WorkbookSourceContext | null): void {
    if (source.value === value) source.value = null
  }

  function setLeftPercent(value: number): void {
    if (!Number.isFinite(value)) return
    leftPercent.value = Math.min(MAX, Math.max(MIN, Math.round(value)))
  }

  watch(leftPercent, (v) => {
    try {
      localStorage.setItem(STORAGE_KEY, String(v))
    } catch {
      /* non-fatal */
    }
  })

  return { leftPercent, setLeftPercent, source, setSource, clearSource }
})
